// Dashboard — real KPIs and two live lists, all scoped to the Desarrollos
// tenant. No charts, no invented deltas: every number on this screen is a
// COUNT or a SUM that came back from Supabase. Zero rows renders an empty
// state, never a placeholder figure.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import { formatUsd, monthStartISO, timeAgoEs } from './format';
import { EmptyText, ErrorText, Panel, SkeletonRows } from './ui';

const KpiCard = ({ label, value, loading }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    {loading ? (
      <div className="mt-2 h-7 w-16 rounded bg-slate-100 animate-pulse" aria-hidden="true" />
    ) : (
      <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1.5">{value}</p>
    )}
  </div>
);

const DashboardHome = ({ onOpenAnexo }) => {
  const [kpis, setKpis] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const monthStart = monthStartISO();
      const countOf = (table) =>
        supabase
          .from(table)
          .select('*', { count: 'exact', head: true })
          .eq('tenant_id', DESARROLLOS_TENANT_ID);

      const [
        clientesCount,
        perfilesCount,
        anexosLiveCount,
        anexosMesCount,
        ingresosRows,
        ultimosClientes,
        anexosLive,
      ] = await Promise.all([
        countOf('customers').is('deleted_at', null),
        countOf('desarrollos_perfil_aduanero').eq('estado', 'activado'),
        countOf('desarrollos_anexos').eq('status', 'live'),
        countOf('desarrollos_anexos')
          .eq('status', 'completed')
          .gte('completed_at', monthStart),
        supabase
          .from('desarrollos_anexos')
          .select('price_usd')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('status', 'completed')
          .eq('payment_status', 'paid')
          .gte('completed_at', monthStart),
        supabase
          .from('customers')
          .select('id, full_name, phone_number, created_at')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('desarrollos_anexos')
          .select('id, client_id, updated_at')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('status', 'live')
          .order('updated_at', { ascending: false })
          .limit(5),
      ]);

      const firstError = [
        clientesCount, perfilesCount, anexosLiveCount, anexosMesCount,
        ingresosRows, ultimosClientes, anexosLive,
      ].find((r) => r.error);
      if (firstError) throw firstError.error;

      // PostgREST has no server-side SUM here, so total the rows we filtered.
      const ingresos = (ingresosRows.data || []).reduce(
        (sum, row) => sum + Number(row.price_usd || 0),
        0
      );

      setKpis({
        clientes: clientesCount.count || 0,
        perfiles: perfilesCount.count || 0,
        anexosLive: anexosLiveCount.count || 0,
        anexosMes: anexosMesCount.count || 0,
        ingresos,
      });
      setClientes(ultimosClientes.data || []);

      // Resolve client names and intake counts for the live-Anexo list with
      // follow-up queries rather than PostgREST embeds — two flat reads are
      // easier to reason about than a nested select, and the row counts here
      // are tiny (5 Anexos).
      const live = anexosLive.data || [];
      if (live.length === 0) {
        setAnexos([]);
      } else {
        const [names, events] = await Promise.all([
          supabase
            .from('customers')
            .select('id, full_name')
            .eq('tenant_id', DESARROLLOS_TENANT_ID)
            .in('id', live.map((a) => a.client_id)),
          supabase
            .from('desarrollos_intake_events')
            .select('anexo_id')
            .eq('tenant_id', DESARROLLOS_TENANT_ID)
            .in('anexo_id', live.map((a) => a.id)),
        ]);
        if (names.error) throw names.error;
        if (events.error) throw events.error;

        const nameById = Object.fromEntries(
          (names.data || []).map((c) => [c.id, c.full_name])
        );
        const eventCount = {};
        (events.data || []).forEach((e) => {
          eventCount[e.anexo_id] = (eventCount[e.anexo_id] || 0) + 1;
        });

        setAnexos(
          live.map((a) => ({
            ...a,
            full_name: nameById[a.client_id] || a.client_id,
            events: eventCount[a.id] || 0,
          }))
        );
      }
    } catch (err) {
      console.error('[Desarrollos/Dashboard] error cargando datos', err);
      setError('No pudimos cargar los datos. Revisá tu conexión y volvé a intentar.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    { label: 'Clientes registrados', value: kpis?.clientes ?? 0 },
    { label: 'Perfiles activados', value: kpis?.perfiles ?? 0 },
    { label: 'Anexos activos', value: kpis?.anexosLive ?? 0 },
    { label: 'Anexos completados (mes)', value: kpis?.anexosMes ?? 0 },
    { label: 'Ingresos del mes', value: formatUsd(kpis?.ingresos) },
  ];

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <ErrorText>{error}</ErrorText>
        <button
          type="button"
          onClick={load}
          className="text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <KpiCard key={c.label} label={c.label} value={c.value} loading={loading} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Panel title="Últimos clientes registrados">
          {loading ? (
            <SkeletonRows />
          ) : clientes.length === 0 ? (
            <EmptyText>
              Ningún cliente registrado todavía. Comenzá creando uno en la pestaña Clientes.
            </EmptyText>
          ) : (
            <div>
              {clientes.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {c.full_name || c.id}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{c.phone_number}</p>
                  </div>
                  <p className="text-[10px] text-slate-400 shrink-0">{timeAgoEs(c.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Anexos activos ahora">
          {loading ? (
            <SkeletonRows />
          ) : anexos.length === 0 ? (
            <EmptyText>
              No hay Anexos en curso. Los que los clientes vayan iniciando van a aparecer acá.
            </EmptyText>
          ) : (
            <div>
              {anexos.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onOpenAnexo(a.id)}
                  className="w-full text-left flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors rounded-lg px-1 -mx-1"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{a.full_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {a.events} {a.events === 1 ? 'dato recibido' : 'datos recibidos'}
                    </p>
                  </div>
                  <p className="text-[10px] text-slate-400 shrink-0">{timeAgoEs(a.updated_at)}</p>
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
};

export default DashboardHome;
